import { css } from "@emotion/react"
import { useContext, useCallback } from "react"
import PropTypes from "prop-types"
import { get as dig } from "lodash"
import { Redirect } from "@reach/router"
import { usePushStream } from "source/shared/PushStream"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { WebBootContext } from "source/publishing/WebBoot"
import { FullscreenLayout } from "source/shared/Layout"
import { Error404 } from "source/shared/Error404"
import OpenGraphTags from "source/shared/OpenGraphTags"
import { useQRCode } from "source/shared/QRCode"

import {
  createResource,
  useCacheHydrate,
  useCacheRead,
} from "source/shared/react-cache-but-its-a-context"
import { RoutingAwareDangerousHTML } from "source/shared/RoutingAwareDangerousHTML"

export const PageResource = createResource(
  ({ slug, version }) => {
    const filter = version === "draft" ? "current_draft" : "current_published"
    const url = `/publishing/v2/pages?filter=${filter}&where[slug]=${slug}`
    return sessionApiClient.get(url).then((response) => response.data[0])
  },
  ({ slug, version }) => `publishing.page.${slug}.${version}`,
)

CustomPage.propTypes = {
  slug: PropTypes.string,
  notFound: PropTypes.node,
}

export function CustomPage({ slug, notFound = <Error404 /> }) {
  const { draftMode } = useContext(WebBootContext)
  const version = draftMode ? "draft" : "published"
  const page = useCacheRead(PageResource, { slug, version })
  const reloadPage = useCacheHydrate(PageResource, { slug, version })

  const conditionallyReloadPage = useCallback(
    (event) => {
      const prevPage = { slug, version }
      const nextPage = {
        slug: dig(event, "data.relationships.slug.data.id"),
        version: dig(event, "data.relationships.version.data.id"),
      }
      if (willBeOnTopOfVersionStack({ prevPage, nextPage })) reloadPage()
    },
    [reloadPage, slug, version],
  )
  usePushStream(conditionallyReloadPage, ["publishing.page.created"])

  return <PageRendering page={page} notFound={notFound} />
}

PageLayout.propTypes = {
  page: PropTypes.object.isRequired,
}
function PageLayout({ page }) {
  const { attributes } = page
  const { title, content, blocks, header_image: headerImage, slug } = attributes

  useQRCode(slug)

  return (
    <>
      <OpenGraphTags
        title={title}
        imageUrl={headerImage?.attributes?.variants?.small}
      />
      {headerImage && (
        <div css={styles.headerImageWrapper}>
          <img
            alt="Page header"
            css={styles.headerImage}
            src={headerImage.attributes.variants.small}
            srcSet={`${headerImage.attributes.variants.small} 640w, ${
              headerImage.attributes.variants.medium
            } 960w, ${headerImage.attributes.variants.large} 2000w`}
          />
        </div>
      )}
      {blocks?.length > 0 ? (
        <FullscreenLayout>
          <RoutingAwareDangerousHTML html={content} />
        </FullscreenLayout>
      ) : (
        <RoutingAwareDangerousHTML html={content} />
      )}
    </>
  )
}

export function PageRendering({ page, notFound }) {
  if (!page) {
    return notFound
  } else if (page.attributes.redirect_uri) {
    return <Redirect to={page.attributes.redirect_uri} noThrow />
  } else {
    return <PageLayout page={page} />
  }
}

function willBeOnTopOfVersionStack({ prevPage, nextPage }) {
  if (prevPage.slug === nextPage.slug) {
    switch (prevPage.version) {
      case "draft":
        return ["draft", "published"].includes(nextPage.version)
      case "published":
        return nextPage.version === "published"
    }
  }

  return false
}

const styles = {
  headerImageWrapper: css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto 24px;
    max-width: 640px;

    @media screen and (max-width: 639px) {
      margin: -24px -24px 24px;
    }
  `,
  headerImage: css`
    display: block;
    width: 100%;
  `,
}
